<template>
  <div>
    <CollectionView :endpoint="utilities.path('communities/@/servers', communityId)" v-model:reload="reload" searchable>
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between g-4">
          <h4 class="mb-0">{{ translate("servers") }}</h4>
          <ButtonView v-if="serverCountUsage.value?.reached === false" type="normal" color="primary" @click="creating = true">
            {{ translate("create") }}
          </ButtonView>
        </div>
      </template>
      <template v-slot:content="{ items }">
        <div class="card">
          <div class="table-responsive">
            <div class="table">
              <div class="table-header">
                <div class="table-header-cell">{{ translate("name") }}</div>
                <div class="table-header-cell">{{ translate("color") }}</div>
                <div class="table-header-cell">{{ translate("addon_version") }}</div>
                <div class="table-header-cell">{{ translate("status") }}</div>
                <div class="table-header-cell">{{ translate("actions") }}</div>
              </div>
              <div class="table-body">
                <div class="table-row" v-for="item in items" :key="item.id">
                  <div class="table-column">
                    {{ item.name }}
                  </div>
                  <div class="table-column">
                    <i class="fas fa-circle" :style="{ color: item.color }"></i>
                  </div>
                  <div class="table-column">
                    {{ item.addonVersion ?? "" }}
                  </div>
                  <div class="table-column">
                    <div v-if="item.online" class="d-flex align-items-center g-2">
                      <i class="fas fa-circle text-success"></i> <span>{{ translate("server.online") }}</span>
                    </div>
                    <div v-else class="d-flex align-items-center g-2">
                      <i class="fas fa-circle text-danger"></i> <span>{{ translate("server.offline") }}</span>
                    </div>
                  </div>
                  <div class="table-column">
                    <div class="d-flex align-items-center g-2">
                      <ButtonView type="soft" color="primary" size="small" @click="viewing = item">
                        <i class="fas fa-eye"></i>
                      </ButtonView>
                      <TargetedModalHttpFormView size="small" :endpoint="utilities.path('servers/@', item.id)" method="delete" @success="reload = true">
                        <template v-slot:target="{ show }">
                          <ButtonView type="soft" color="danger" size="small" @click="show">
                            <i class="fas fa-trash"></i>
                          </ButtonView>
                        </template>
                        <template v-slot:form="{ submit, loading, hide }">
                          <div class="mb-4">{{ translate("community.server.delete.explanation") }}</div>
                          <div class="d-flex align-items-center g-4">
                            <ButtonView type="normal" color="danger" class="w-100" @click="submit">
                              {{ translate("delete") }}
                            </ButtonView>
                            <ButtonView type="normal" color="secondary" class="w-100" @click="hide">
                              {{ translate("cancel") }}
                            </ButtonView>
                          </div>
                          <SpinnerView :active="loading" color="danger"/>
                        </template>
                      </TargetedModalHttpFormView>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </CollectionView>
    <div class="row g-4 mt-4">
      <HttpContentView :endpoint="utilities.path('communities/@/server-addons/latest', communityId)">
        <template v-slot:content="{ data }">
          <div class="col-12 col-lg-6">
            <div class="card">
              <div class="card-body">
                <h3 class="mb-0 d-flex align-items-center justify-content-between g-3">
                  <span class="d-flex align-items-center g-2">
                    <i class="fas fa-puzzle-piece text-primary"></i>
                    <span>{{ translate('server_addon') }}</span>
                  </span>
                  <span>
                    {{ data.version }}
                  </span>
                </h3>
                <div class="d-flex align-items-center g-4 mt-4" v-if="data.fileUrl || translate('tutorials.videos.setup.' + info.community?.type.key) !== ('tutorials.videos.setup.' + info.community?.type.key)">
                  <a v-if="data.fileUrl" class="btn btn-normal btn-primary w-100" :href="data.fileUrl" target="_blank">
                    <div class="d-flex align-items-center justify-content-center g-2">
                      <i class="fas fa-download"></i>
                      <span>{{ translate("download") }}</span>
                    </div>
                  </a>
                  <VideoTutorialButton v-if="translate('tutorials.videos.setup.' + info.community?.type.key) !== ('tutorials.videos.setup.' + info.community?.type.key)" class="w-100" :title="translate('view_setup_tutorial')" :url="translate('tutorials.videos.setup.' + info.community?.type.key)"/>
                </div>
              </div>
            </div>
          </div>
        </template>
      </HttpContentView>
      <div class="col-12 col-lg-6">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between">
              <h3 class="mb-0">
                <i class="fas fa-sort-amount-up text-primary me-3 fa-lg"></i>
                <span>{{ translate("community.servers.count.usage") }}</span>
              </h3>
              <div v-if="serverCountUsage.value" style="font-size: 1.1em">
                <span :class="{ 'text-danger': serverCountUsage.value.exceeded }">{{ serverCountUsage.value.value }}</span> / <span>{{ serverCountUsage.value.limit }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ModalView :active="viewing !== undefined" @update:active="viewing = undefined">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <h3 class="mb-0">{{ translate("server") }}</h3>
          <ModalCloseButtonView @click="viewing = undefined"/>
        </div>
      </template>
      <template v-slot:body>
        <ServerView :community-id="communityId" :server-id="viewing.id" :server-count-usage="serverCountUsage" @setup="viewing = undefined; settingUp = $event.server" @update="reload = true"/>
      </template>
    </ModalView>
    <ModalView v-model:active="creating">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <h3 class="mb-0">{{ translate("server") }}</h3>
          <ModalCloseButtonView @click="creating = false"/>
        </div>
      </template>
      <template v-slot:body>
        <ServerFormView :community-id="communityId" :creating="creating" @dismiss="creating = false" @create="creating = false; settingUp = $event.server; reload = true"/>
      </template>
    </ModalView>
    <ModalView :active="settingUp !== undefined">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between g-4">
          <h3 class="mb-0">{{ translate("setup") }}</h3>
          <ModalCloseButtonView @click="settingUp = undefined"/>
        </div>
      </template>
      <template v-slot:body>
        <ServerSetupView :server="settingUp" @complete="settingUp = undefined; reload = true"/>
      </template>
    </ModalView>
  </div>
</template>

<script setup>
import {CollectionView, ModalView, ButtonView, HttpContentView, SpinnerView, utilities, TargetedModalHttpFormView, ModalCloseButtonView} from "@/niftybit";
import VideoTutorialButton from "@/components/other/VideoTutorialButton.vue";
import {onMounted, onUnmounted, ref, watch} from "vue";
import usage from "@/systems/usage/usage";
import info from "@/info";
import reloadRoute from "@/reloadRoute";
import ServerSetupView from "@/systems/community/views/ServerSetupView.vue";
import ServerView from "@/systems/community/views/ServerView.vue";
import ServerFormView from "@/systems/community/views/ServerFormView.vue";
import {useRoute} from "vue-router";

defineProps({
  communityId: String
});

const route = useRoute();

const reload = ref(false);
const creating = ref(route.query.creating === "true");
const settingUp = ref(undefined);
const viewing = ref(undefined);

const serverCountUsage = usage.value("modules.servers.serverCount");

watch(reload, () => reload.value ? serverCountUsage.value.reload() : undefined);

function onRouteReload() {
  reload.value = true;
}

onMounted(() => {
  reloadRoute.subscribe(onRouteReload);
});

onUnmounted(() => {
  reloadRoute.unsubscribe(onRouteReload);
});

</script>